import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Paper,
  Typography,
  Grid,
  CssBaseline,
  LinearProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import LoadingButton from "@mui/lab/LoadingButton";
import logo from "../assets/logo_no_background.png";
import {
  acceptPartnerOwnerInvitation,
  acceptPartnerUserInvitation,
} from "../services/partnerInvitation.ts";
import { resetPassword } from "../services/partnerUser.ts";

interface ShowPasswordState {
  password: boolean;
  confirmPassword: boolean;
}

interface PasswordStrength {
  score: number; // 0‑100
  messages: string[];
}

export default function PartnerSetup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState<ShowPasswordState>({
    password: false,
    confirmPassword: false,
  });
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    messages: [],
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const invitationCode = searchParams.get("code") || "";
  const partnerNumber = searchParams.get("partnerNumber") || "";
  const action = searchParams.get("action");
  const email = searchParams.get("email") || "";
  const token = searchParams.get("token") || searchParams.get("id") || "";
  const isUser = !!partnerNumber;
  const isReset = action === "reset";

  const handleClickShowPassword = (key: keyof ShowPasswordState) =>
    setShowPassword((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const checkPasswordStrength = (password: string): PasswordStrength => {
    const strength: PasswordStrength = { score: 0, messages: [] };
    if (password.length < 8) strength.messages.push("At least 8 characters");
    else strength.score += 25;
    if (!/\d/.test(password)) strength.messages.push("At least one number");
    else strength.score += 25;
    if (!/[a-z]/.test(password))
      strength.messages.push("At least one lowercase letter");
    else strength.score += 25;
    if (!/[A-Z]/.test(password))
      strength.messages.push("At least one uppercase letter");
    else strength.score += 25;
    return strength;
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordStrength(checkPasswordStrength(event.target.value));
  };

  const getStrengthColor = (score: number) => {
    if (score <= 25) return "#f44336";
    if (score <= 50) return "#ff9800";
    if (score <= 75) return "#ffc107";
    return "#4caf50";
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    const data = new FormData(event.currentTarget);
    const password = data.get("password") as string;
    const confirmPassword = data.get("confirmPassword") as string;

    if (passwordStrength.score < 100) {
      setError("Please ensure your password meets all requirements");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (isReset) {
      if (!email || !token || !partnerNumber) {
        setError("Invalid reset link");
        return;
      }
    } else {
      if (!invitationCode) {
        setError("Invalid invitation link");
        return;
      }
    }

    setLoading(true);
    try {
      if (isReset) {
        const res = await resetPassword({
          partnerNumber,
          email,
          verifyCode: token,
          password,
          isEncrypted: false,
        });
        if (res.error) {
          setError(res.error);
        } else {
          setSuccess(
            res.message || "Password reset successful! Redirecting to login..."
          );
          setTimeout(() => navigate("/login"), 1500);
        }
      } else if (isUser) {
        await acceptPartnerUserInvitation(
          partnerNumber,
          invitationCode,
          password
        );
        setSuccess("Password setup successful! Redirecting to login...");
        setTimeout(() => navigate("/login"), 1500);
      } else {
        await acceptPartnerOwnerInvitation(invitationCode, password);
        setSuccess("Password setup successful! Redirecting to login...");
        setTimeout(() => navigate("/login"), 1500);
      }
    } catch (err: unknown) {
      let msg = isReset
        ? "Failed to reset password"
        : "Failed to setup password";
      if (err instanceof Error) {
        msg = err.message;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container component="main" sx={{ height: "100vh" }}>
      <CssBaseline />

      {/* 左侧：设置密码表单 */}
      <Grid item xs={12} sm={8} md={5}>
        <Paper
          elevation={6}
          square
          sx={{
            height: "100%",
            width: "100%", // ★ 填满左侧网格
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              my: 8,
              mx: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box sx={{ mb: 4 }}>
              <img src={logo} alt="Logo" style={{ height: 90 }} />
            </Box>
            <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
              {isReset ? "Reset Your Password" : "Setup Your Password"}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 4 }}>
              {isReset
                ? "Please enter a new password for your account"
                : "Please create a password for your account"}
            </Typography>

            {/* 表单 */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ mt: 1, width: "100%", maxWidth: 400 }}
            >
              {/* 密码输入 */}
              <FormControl fullWidth variant="outlined" sx={{ mb: 1 }}>
                <InputLabel htmlFor="password">Create Password *</InputLabel>
                <OutlinedInput
                  id="password"
                  name="password"
                  type={showPassword.password ? "text" : "password"}
                  required
                  onChange={handlePasswordChange}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => handleClickShowPassword("password")}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword.password ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Create Password *"
                />
              </FormControl>

              {/* 密码强度条 */}
              <Box sx={{ mb: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={passwordStrength.score}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#e0e0e0",
                    "& .MuiLinearProgress-bar": {
                      bgcolor: getStrengthColor(passwordStrength.score),
                    },
                  }}
                />
                <Box sx={{ mt: 1 }}>
                  {passwordStrength.messages.map((m, i) => (
                    <Typography
                      key={i}
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      • {m}
                    </Typography>
                  ))}
                  {passwordStrength.score === 100 && (
                    <Typography
                      variant="caption"
                      color="success.main"
                      display="block"
                      sx={{ mt: 0.5 }}
                    >
                      Password strength: Strong
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* 确认密码 */}
              <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
                <InputLabel htmlFor="confirmPassword">
                  Confirm Password *
                </InputLabel>
                <OutlinedInput
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword.confirmPassword ? "text" : "password"}
                  required
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() =>
                          handleClickShowPassword("confirmPassword")
                        }
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword.confirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Confirm Password *"
                />
              </FormControl>

              {/* 提示信息 */}
              {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                  {error}
                </Typography>
              )}
              {success && (
                <Typography color="success.main" sx={{ mb: 2 }}>
                  {success}
                </Typography>
              )}

              {/* 提交按钮 */}
              <LoadingButton
                type="submit"
                fullWidth
                loading={loading}
                variant="contained"
                sx={{ mt: 1, mb: 2 }}
              >
                Set Password
              </LoadingButton>
              <Button
                fullWidth
                variant="text"
                onClick={() => navigate("/login")}
                sx={{ textTransform: "none" }}
              >
                Back to Login
              </Button>
            </Box>
          </Box>
        </Paper>
      </Grid>

      {/* 右侧：欢迎横幅 */}
      <Grid
        item
        sm={4}
        md={7}
        sx={{
          display: { xs: "none", sm: "flex" }, // ★ 用 flex 让子元素居中
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#4272f5",
          flexGrow: 1, // ← 让它吃掉剩余空间
          minWidth: 0, // ← 避免因内容宽度限制而收缩
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            px: 4,
            maxWidth: 600,
          }}
        >
          <Typography variant="h3" fontWeight="bold" color="#fff" mb={3}>
            Welcome to entrio Partner Portal
          </Typography>
          <Typography variant="h6" color="#fff">
            Collaborative solutions for shared success
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
}
